import { useEffect, useState } from 'react'
import { message, Switch } from 'antd'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { useAppStore } from '@forwardflow/store'
import useSWR from 'swr'
import Icon from '@forwardflow/ui/Icons'

const ChannelConfig = () => {
   const { currentOrganization } = useAppStore()

   const { data: orgDetails, mutate } = useSWR(
      currentOrganization
         ? ['/organization-account/details', currentOrganization]
         : null,
      () => OrganizationService.getOrganizationDetails({ page: 1, limit: 1 })
   )

   const channelConfig =
      orgDetails?.deploymentConfig?.channelConfig ?? null

   const [phoneCallEnabled, setPhoneCallEnabled] = useState(true)
   const [voicemailEnabled, setVoicemailEnabled] = useState(true)
   const [smsEnabled, setSmsEnabled] = useState(true)
   const [saving, setSaving] = useState(false)

   useEffect(() => {
      if (channelConfig) {
         setPhoneCallEnabled(channelConfig.phoneCallEnabled ?? true)
         setVoicemailEnabled(channelConfig.voicemailEnabled ?? true)
         setSmsEnabled(channelConfig.smsEnabled ?? true)
      }
   }, [channelConfig])

   const handleUpdate = async (update: {
      phoneCallEnabled?: boolean
      voicemailEnabled?: boolean
      smsEnabled?: boolean
   }) => {
      setSaving(true)
      try {
         await OrganizationService.updateChannelConfig(update)
         mutate()
      } catch (error: any) {
         message.error(
            error?.response?.data?.message || 'Failed to update channel config'
         )
      } finally {
         setSaving(false)
      }
   }

   const handlePhoneCallToggle = (checked: boolean) => {
      setPhoneCallEnabled(checked)
      if (!checked) {
         setVoicemailEnabled(false)
         handleUpdate({
            phoneCallEnabled: false,
            voicemailEnabled: false,
         })
      } else {
         handleUpdate({ phoneCallEnabled: true })
      }
   }

   const handleVoicemailToggle = (checked: boolean) => {
      setVoicemailEnabled(checked)
      handleUpdate({ voicemailEnabled: checked })
   }

   const handleSmsToggle = (checked: boolean) => {
      setSmsEnabled(checked)
      handleUpdate({ smsEnabled: checked })
   }

   return (
      <div className="p-6 mt-6 rounded-lg bg-[#828282]/10 shadow-lg max-w-xl">
         <div className="mb-6">
            <p className="font-medium text-white mb-1">
               Communication Channels
            </p>
            <p className="text-sm text-white/70">
               Enable or disable communication channels for this organization.
               Disabled channels will be skipped during campaign execution.
            </p>
         </div>

         <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
               <div className="flex items-center gap-3">
                  <Icon name="Telephone" className="fill-white scale-75" />
                  <div>
                     <p className="font-medium text-white text-sm">
                        Phone Calls
                     </p>
                     <p className="text-xs text-white/50">
                        Enable outbound phone calls to leads
                     </p>
                  </div>
               </div>
               <Switch
                  checked={phoneCallEnabled}
                  onChange={handlePhoneCallToggle}
                  disabled={saving}
                  className="bg-white/20"
               />
            </div>

            {phoneCallEnabled && (
               <div className="flex items-center justify-between p-4 ml-8 rounded-lg bg-white/5">
                  <div className="flex items-center gap-3">
                     <Icon name="Telephone" className="fill-white scale-75" />
                     <div>
                        <p className="font-medium text-white text-sm">
                           Voicemail
                        </p>
                        <p className="text-xs text-white/50">
                           Leave voicemail when calls are unanswered
                        </p>
                     </div>
                  </div>
                  <Switch
                     checked={voicemailEnabled}
                     onChange={handleVoicemailToggle}
                     disabled={saving}
                     className="bg-white/20"
                  />
               </div>
            )}

            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
               <div className="flex items-center gap-3">
                  <Icon name="Message" className="fill-white scale-75" />
                  <div>
                     <p className="font-medium text-white text-sm">
                        SMS / Text Messages
                     </p>
                     <p className="text-xs text-white/50">
                        Enable text message campaigns to leads
                     </p>
                  </div>
               </div>
               <Switch
                  checked={smsEnabled}
                  onChange={handleSmsToggle}
                  disabled={saving}
                  className="bg-white/20"
               />
            </div>
         </div>
      </div>
   )
}

export default ChannelConfig
